import { Card, CardContent } from "@bcc/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@bcc/components/ui/tooltip";
import { CreditCard, DollarSign, LucideIcon, Package, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { setDashboardStatsTestId } from "./dashboard-stats.utils";
import { SlaTrackerCard } from "./sla-tracker-card";

interface SlaImpact {
  score: number;
  impact: number;
  status: "excellent" | "fair" | "risk";
  message: string;
}

export interface StatData {
  name: string;
  baseValue: number;
  value: string;
  change: string;
  changeType: "positive" | "negative" | "neutral";
  description: string;
  icon: string;
  sparklineData: Array<{ day: number; value: number }>;
  color: string;
  bgColor: string;
  sla: SlaImpact;
}

interface DashboardStatsProps {
  readonly data: StatData[];
  readonly testId?: string;
}

const iconMap: Record<string, LucideIcon> = {
  DollarSign,
  ShoppingBag,
  Package,
  CreditCard,
};

const getSlaColor = (status: "excellent" | "fair" | "risk") => {
  switch (status) {
    case "excellent":
      return "#22C55E";
    case "fair":
      return "#F59E0B";
    case "risk":
      return "#EF4444";
  }
};

interface StatCardProps {
  readonly stat: StatData;
  readonly index: number;
}

const slaLabelKeys: Record<SlaImpact["status"], string> = {
  excellent: "dashboardComponents.dashboardStats.excellent",
  fair: "dashboardComponents.dashboardStats.fair",
  risk: "dashboardComponents.dashboardStats.atRisk",
};

const changeTypeColorClasses: Record<StatData["changeType"], string> = {
  positive: "text-[#22C55E]",
  negative: "text-[#DC2626]",
  neutral: "text-[#6B7280]",
};

function StatCard({ stat, index }: StatCardProps) {
  const { t } = useTranslation();
  const IconComponent = iconMap[stat.icon] || DollarSign;
  const slaColor = getSlaColor(stat.sla.status);
  const slaLabel = t(slaLabelKeys[stat.sla.status]) as string;
  const changeColorClass = changeTypeColorClasses[stat.changeType];

  return (
    <div data-testid={setDashboardStatsTestId(`stat-card-${index}`)}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="transition-all duration-200 hover:shadow-md hover:-translate-y-1 cursor-pointer border-[#E5E7EB] relative overflow-hidden">
              <CardContent className="p-6">
                <div className="space-y-3">
                  {/* Header with icon */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[#6B7280]" data-testid={setDashboardStatsTestId(`stat-label-${index}`)}>
                      {stat.name}
                    </p>
                    <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${stat.bgColor}`}>
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Value and change */}
                  <div className="space-y-1">
                    <p
                      className="text-[28px] font-bold leading-none tracking-tight text-[#111827]"
                      data-testid={setDashboardStatsTestId(`stat-value-${index}`)}
                    >
                      {stat.value}
                    </p>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-medium ${changeColorClass}`}
                        data-testid={setDashboardStatsTestId(`stat-change-${index}`)}
                      >
                        {stat.change}
                      </span>
                      {stat.description && <span className="text-xs text-[#6B7280]">{stat.description}</span>}
                    </div>
                  </div>

                  {/* Sparkline */}
                  <div className="h-12 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stat.sparklineData}>
                        <Line type="monotone" dataKey="value" stroke={stat.color} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* SLA impact bar at the bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#E5E7EB]">
                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${stat.sla.impact * 100}%`,
                      background: `linear-gradient(to right, #0D9488, ${slaColor})`,
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{t("dashboardComponents.dashboardStats.slaImpact")}:</span>
                <span style={{ color: slaColor }}>{slaLabel}</span>
              </div>
              <p className="text-xs text-muted-foreground">{stat.sla.message}</p>
              <div className="text-xs">
                <span className="text-muted-foreground">{t("dashboardComponents.dashboardStats.slaScore")}: </span>
                <span className="font-medium">{stat.sla.score}%</span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export function DashboardStats({ data, testId }: DashboardStatsProps) {
  return (
    <div data-testid={testId ?? setDashboardStatsTestId("container")} className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {data.map((stat, index) => (
        <StatCard key={stat.name} stat={stat} index={index} />
      ))}

      <SlaTrackerCard />
    </div>
  );
}
